import { CacheStrategy } from "../api/CacheApi";
import { Observable } from "../state/Observable";

import type { ReactiveController } from "@lit/reactive-element";
import type { WishlistElement } from "../WishlistElement";
import type { ProductInfo } from "../state/WishlistState";
import type { WishlistElementHeadless } from "../WishlistElementHeadless";
import type { ShopifyCart } from "../api/ShopifyApi";
import type { Wishlist, WishlistItem } from "../api/WishlistApi";

export class WishlistStateController implements ReactiveController {
  host: WishlistElement | WishlistElementHeadless;
  stateConfig: ParsedStateConfig = { loading: "eager" };
  state: State = {};

  get app() {
    if (!window.WishlistKing) {
      throw new Error("Wishlist King SDK not initialized");
    }

    return window.WishlistKing!;
  }

  get hostDataset() {
    if ((this.host as WishlistElement).dataset) {
      return (this.host as WishlistElement).dataset;
    }

    if ((this.host as WishlistElementHeadless).host) {
      return (this.host as WishlistElementHeadless).host.dataset;
    }

    return {};
  }

  constructor(host: WishlistElement | WishlistElementHeadless) {
    (this.host = host).addController(this);
  }

  hostConnected() {}

  hostDisconnected() {}

  parseStateConfig(config: StateConfig): ParsedStateConfig {
    return {
      loading: config.loading ?? "eager",
      wishlist: this.parseWishlistStateConfig(config),
      wishlistItem: this.parseWishlistItemStateConfig(config),
      productInfo: this.parseProductInfoStateConfig(config),
      cart: config.cart,
    };
  }

  parseWishlistStateConfig({
    wishlist,
  }: StateConfig): WishlistStateConfig | undefined {
    const wishlistId = this.hostDataset.wishlistId ?? "mine";

    if (!wishlist) {
      return;
    }

    return {
      params: {
        wishlistId,
      },
    };
  }

  parseWishlistItemStateConfig({
    wishlistItem,
  }: StateConfig): WishlistItemStateConfig | undefined {
    const { wishlistId = "mine", wishlistItemId } = this.hostDataset;

    if (!wishlistItem || !wishlistItemId) {
      return;
    }

    return {
      params: {
        wishlistId,
        wishlistItemId,
      },
    };
  }

  parseProductInfoStateConfig({
    productInfo,
  }: StateConfig): ProductInfoStateConfig | undefined {
    if (!productInfo) {
      return;
    }

    const {
      wishlistId = "mine",
      productHandle = "",
      productId,
      variantId,
    } = this.hostDataset;

    return {
      params: {
        wishlistId,
        productHandle,
        variantId: variantId ? parseInt(variantId) : undefined,
      },
    };
  }

  observe() {
    this.stateConfig = this.parseStateConfig(this.host.getStateConfig());

    // TODO: Avoid re-render when data did not change
    // distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))

    return new Observable<State>((subscriber) => {
      const next = (state: Partial<State>) => {
        this.state = {
          ...this.state,
          ...state,
        };
        subscriber.next(this.state);
      };

      if (this.stateConfig.wishlist) {
        const { wishlistId = "mine" } = this.stateConfig.wishlist.params;
        this.app.state
          .observeWishlist({
            wishlistId,
          })
          .subscribe((wishlist) => next({ wishlist }));
      }

      if (this.stateConfig.productInfo) {
        const { productHandle, variantId } =
          this.stateConfig.productInfo.params;

        if (!productHandle) {
          throw new Error("Product handle is required");
        }

        this.app.state
          .observeProductInfo({
            productHandle,
            variantId,
          })
          .subscribe((productInfo) => next({ productInfo }));
      }

      if (this.stateConfig.wishlistItem) {
        const { wishlistId, wishlistItemId } =
          this.stateConfig.wishlistItem.params;

        if (!wishlistId) {
          throw new Error("Wishlist id is required");
        }

        if (!wishlistItemId) {
          throw new Error("Wishlist item id is required");
        }

        this.app.state
          .observeWishlist({ wishlistId: wishlistId })
          .subscribe((wishlist) =>
            next({
              wishlistItem: wishlist.items.find(
                (item) => item.id === wishlistItemId,
              ),
            }),
          );
      }

      if (this.stateConfig.cart) {
        this.app.state
          .observeShopifyCart({
            cacheStrategy: CacheStrategy.Short,
          })
          .subscribe((cart) => next({ cart }));
      }
    });
  }

  getFallback() {
    this.stateConfig = this.parseStateConfig(this.host.getStateConfig());

    const fallbackState: State = {};

    if (this.stateConfig.wishlist) {
      const { wishlistId = "mine" } = this.stateConfig.wishlist.params;

      fallbackState.wishlist = {
        id: wishlistId,
        isMine: wishlistId === "mine",
        numItems: 0,
        items: [],
      };
    }

    if (this.stateConfig.productInfo) {
      const { variantId = 0, productHandle } =
        this.stateConfig.productInfo.params;

      fallbackState.productInfo = {
        productId: 0,
        variantId,
        productHandle,
        inWishlist: false,
      };
    }

    if (this.stateConfig.wishlistItem) {
      const { wishlistId, wishlistItemId } =
        this.stateConfig.wishlistItem.params;

      if (!wishlistId) {
        throw new Error("Wishlist id is required");
      }

      if (!wishlistItemId) {
        throw new Error("Wishlist item id is required");
      }

      fallbackState.wishlistItem = {
        id: wishlistItemId,
        product: {
          id: 0,
          handle: "",
          variants: [],
        },
      };
    }

    return fallbackState;
  }
}

export interface State {
  wishlist?: Wishlist;
  productInfo?: ProductInfo;
  wishlistItem?: WishlistItem;
  cart?: ShopifyCart;
}

export interface StateConfig {
  loading?: StateLoadingStrategy;
  wishlist?: boolean;
  wishlistItem?: boolean;
  productInfo?: boolean;
  cart?: boolean;
}

export interface ParsedStateConfig {
  loading: StateLoadingStrategy;
  wishlist?: WishlistStateConfig;
  wishlistItem?: WishlistItemStateConfig;
  productInfo?: ProductInfoStateConfig;
  cart?: boolean;
}

export interface WishlistStateConfig {
  params: {
    wishlistId: string;
  };
}

export interface ProductInfoStateConfig {
  params: {
    wishlistId: string;
    productHandle: string;
    variantId?: number;
  };
}

export interface WishlistItemStateConfig {
  params: {
    wishlistId: string;
    wishlistItemId: string;
  };
}

export type StateLoadingStrategy = "eager" | "lazy";
